<template>
  <div class="push-monitor-page">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/" class="page-breadcrumb">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>运维管理</el-breadcrumb-item>
      <el-breadcrumb-item>推送监控</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 顶部状态卡 -->
    <el-card class="status-card" shadow="hover">
      <div class="status-header">
        <div class="status-badge-wrap">
          <span class="status-badge" :class="statusClass">
            <i :class="statusIcon"></i>
            {{ statusText }}
          </span>
          <span v-if="state.paused" class="paused-info">
            暂停于 {{ $getDateTime(state.pausedAt) }}（{{ pauseReasonText }}）
          </span>
        </div>
        <div class="status-actions">
          <el-button v-if="!state.paused" type="warning" size="small" icon="el-icon-video-pause"
                     @click="handleManualPause" :loading="actionLoading">手动暂停</el-button>
          <el-button v-else type="success" size="small" icon="el-icon-video-play"
                     @click="handleManualResume" :loading="actionLoading">立即恢复</el-button>
          <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadStatus" plain>刷新</el-button>
        </div>
      </div>

      <div class="metric-grid">
        <div class="metric-box">
          <div class="metric-label">连续失败</div>
          <div class="metric-value" :class="{ 'metric-danger': state.consecutiveFailures > 0 }">
            {{ state.consecutiveFailures || 0 }}
          </div>
          <div class="metric-sub">阈值 {{ config.maxConsecutiveFailures || 3 }}</div>
        </div>
        <div class="metric-box">
          <div class="metric-label">累计成功</div>
          <div class="metric-value metric-success">{{ state.totalSent || 0 }}</div>
          <div class="metric-sub">24h: {{ last24h.sent || 0 }}</div>
        </div>
        <div class="metric-box">
          <div class="metric-label">累计失败</div>
          <div class="metric-value metric-danger">{{ state.totalFailed || 0 }}</div>
          <div class="metric-sub">24h: {{ last24h.failed || 0 }}</div>
        </div>
        <div class="metric-box">
          <div class="metric-label">队列积压</div>
          <div class="metric-value" :class="{ 'metric-warning': totalWaiting > 0 }">{{ totalWaiting }}</div>
          <div class="metric-sub">待发 {{ pendingCount }} / 重试 {{ queueCounts.delayed || 0 }}</div>
        </div>
      </div>

      <div v-if="state.paused" class="paused-detail">
        <el-alert type="error" show-icon :closable="false">
          <template slot="title">
            <div>队列已暂停 · {{ pauseReasonText }}</div>
          </template>
          <div class="paused-body">
            <div><b>最近错误：</b>{{ state.lastFailureMessage || '-' }}</div>
            <div><b>最近探活：</b>{{ state.lastProbeAt ? $getDateTime(state.lastProbeAt) : '未探活' }}
              <el-tag v-if="state.lastProbeSuccess === true" type="success" size="mini">成功</el-tag>
              <el-tag v-else-if="state.lastProbeSuccess === false" type="danger" size="mini">失败</el-tag>
            </div>
            <div class="paused-actions">
              <el-button type="primary" size="mini" @click="handleTriggerProbe" :loading="actionLoading">
                <i class="el-icon-connection"></i> 立即探活
              </el-button>
              <span class="hint">系统每 {{ Math.round((config.probeIntervalMs || 300000) / 60000) }} 分钟自动探活一次</span>
            </div>
          </div>
        </el-alert>
      </div>

      <div v-if="state.lastFailureMessage && !state.paused" class="recent-error">
        <el-alert type="warning" show-icon :closable="false">
          <template slot="title">最近失败：{{ state.lastFailureMessage }}</template>
          <div>时间：{{ state.lastFailureAt ? $getDateTime(state.lastFailureAt) : '-' }}</div>
        </el-alert>
      </div>
    </el-card>

    <!-- 主内容区 Tabs -->
    <el-card class="content-card" shadow="hover">
      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <!-- Tab 1: 队列实况 -->
        <el-tab-pane name="jobs">
          <span slot="label"><i class="el-icon-s-order"></i> 队列实况</span>
          <div class="tab-toolbar">
            <el-radio-group v-model="jobState" size="small" @change="loadJobs">
              <el-radio-button label="waiting">等待中 ({{ pendingCount }})</el-radio-button>
              <el-radio-button label="active">处理中 ({{ queueCounts.active || 0 }})</el-radio-button>
              <el-radio-button label="delayed">延迟中 ({{ queueCounts.delayed || 0 }})</el-radio-button>
              <el-radio-button label="failed">已失败 ({{ queueCounts.failed || 0 }})</el-radio-button>
              <el-radio-button label="completed">已完成</el-radio-button>
            </el-radio-group>
            <el-checkbox v-model="autoRefresh" size="small" style="margin-left: 12px;">自动刷新（5s）</el-checkbox>
          </div>
          <el-table :data="jobList" border stripe size="small" v-loading="jobLoading" style="width: 100%;">
            <el-table-column prop="id" label="JobId" width="180" show-overflow-tooltip></el-table-column>
            <el-table-column label="来源" width="140">
              <template slot-scope="scope">
                <el-tag size="mini" :type="sourceTagType(scope.row.source)">{{ sourceLabel(scope.row.source) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="sentBy" label="发送人" width="140" show-overflow-tooltip></el-table-column>
            <el-table-column label="优先级" width="90" align="center">
              <template slot-scope="scope">
                <el-tag size="mini" :type="priorityTagType(scope.row.priority)">{{ priorityLabel(scope.row.priority) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="attemptsMade" label="重试" width="70" align="center"></el-table-column>
            <el-table-column label="入队时间" width="160" align="center">
              <template slot-scope="scope">{{ scope.row.timestamp ? $getDateTime(new Date(scope.row.timestamp)) : '-' }}</template>
            </el-table-column>
            <el-table-column label="目标群" width="160" show-overflow-tooltip>
              <template slot-scope="scope">{{ convName(scope.row.conversationId) }}</template>
            </el-table-column>
            <el-table-column prop="failedReason" label="失败原因" min-width="150" show-overflow-tooltip></el-table-column>
            <el-table-column label="操作" width="70" align="center">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="showJobDetail(scope.row)">详情</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination @current-change="handleJobPageChange" :current-page="jobPagenum" :page-size="jobPagesize"
                         :total="jobTotal" layout="total, prev, pager, next" class="page-pagination"></el-pagination>
        </el-tab-pane>

        <!-- Tab 2: 参数配置 -->
        <el-tab-pane name="setting">
          <span slot="label"><i class="el-icon-setting"></i> 参数配置</span>
          <el-form :model="configForm" label-width="150px" size="small" class="config-form" v-loading="settingLoading">
            <el-divider content-position="left">推送模式</el-divider>
            <el-form-item label="推送模式">
              <el-radio-group v-model="configForm.pushMode">
                <el-radio label="queue">队列模式（推荐）</el-radio>
                <el-radio label="direct">直连模式（回滚用）</el-radio>
              </el-radio-group>
              <div class="form-hint">切换到 direct 后所有消息立即发送，不经过队列（仅在队列异常时使用）</div>
            </el-form-item>

            <el-divider content-position="left">节流与熔断</el-divider>
            <el-form-item label="发送间隔（ms）">
              <el-input-number v-model="configForm.intervalMs" :min="200" :max="60000" :step="100"></el-input-number>
              <span class="form-hint-inline">每条消息之间的等待时间，范围 200-60000，默认 2000ms</span>
            </el-form-item>
            <el-form-item label="熔断阈值">
              <el-input-number v-model="configForm.maxConsecutiveFailures" :min="1" :max="20"></el-input-number>
              <span class="form-hint-inline">连续失败达到该值触发暂停，默认 3</span>
            </el-form-item>
            <el-form-item label="恢复自动补发">
              <el-switch v-model="configForm.autoResendOnResume"></el-switch>
              <span class="form-hint-inline">队列恢复时自动补发故障期间失败的日报/差评（巡店/手工消息不自动补，可在失败消息中手动重发）</span>
            </el-form-item>
            <el-form-item label="探活间隔（ms）">
              <el-input-number v-model="configForm.probeIntervalMs" :min="60000" :max="3600000" :step="60000"></el-input-number>
              <span class="form-hint-inline">暂停后自动探活频率，默认 300000ms（5分钟）</span>
            </el-form-item>
            <el-form-item label="探活目标群">
              <el-select v-model="configForm.probeConversationId" filterable clearable placeholder="请选择运维群" style="width: 320px;">
                <el-option v-for="g in allGroups" :key="g.conversation_id" :label="g.nickname" :value="g.conversation_id"></el-option>
              </el-select>
              <div class="form-hint">暂停期间心跳消息发到此群，用于自动恢复判定。必须配置才能启用自动恢复。</div>
            </el-form-item>
            <el-form-item label="探活消息">
              <el-input v-model="configForm.probeMessage" style="width: 400px;" maxlength="200" show-word-limit></el-input>
            </el-form-item>

            <el-divider content-position="left">邮件告警</el-divider>
            <el-form-item label="启用邮件告警">
              <el-switch v-model="configForm.mail.enabled"></el-switch>
            </el-form-item>
            <el-form-item label="SMTP 主机">
              <el-input v-model="configForm.mail.host" style="width: 260px;"></el-input>
              <span class="form-hint-inline">默认 smtp.qq.com</span>
            </el-form-item>
            <el-form-item label="SMTP 端口">
              <el-input-number v-model="configForm.mail.port" :min="1" :max="65535"></el-input-number>
              <el-checkbox v-model="configForm.mail.secure" style="margin-left: 12px;">SSL</el-checkbox>
            </el-form-item>
            <el-form-item label="发件邮箱">
              <el-input v-model="configForm.mail.user" style="width: 260px;" placeholder="例如 xxx@qq.com"></el-input>
            </el-form-item>
            <el-form-item label="邮箱授权码">
              <el-input v-model="configForm.mail.pass" type="password" show-password style="width: 260px;"
                        placeholder="QQ邮箱授权码，非登录密码"></el-input>
            </el-form-item>
            <el-form-item label="发件人显示">
              <el-input v-model="configForm.mail.from" style="width: 260px;" placeholder='留空默认"飞鸟中台告警"'></el-input>
            </el-form-item>
            <el-form-item label="收件人">
              <el-input v-model="mailToStr" style="width: 400px;" placeholder="多个邮箱用英文逗号分隔"></el-input>
            </el-form-item>

            <el-form-item>
              <el-button type="primary" @click="saveSetting" :loading="saveLoading">保存配置</el-button>
              <el-button @click="testMail" :loading="testMailLoading">发送测试邮件</el-button>
              <el-button @click="loadSetting">重置</el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <!-- Tab 3: 运维操作 -->
        <el-tab-pane name="ops">
          <span slot="label"><i class="el-icon-s-tools"></i> 运维操作</span>
          <div class="ops-grid">
            <el-card class="ops-card" shadow="hover">
              <div class="ops-title"><i class="el-icon-video-pause" style="color:#e6a23c;"></i> 手动暂停</div>
              <div class="ops-desc">立即暂停队列派发新消息，已在处理中的消息会跑完。</div>
              <el-button type="warning" size="small" @click="handleManualPause" :loading="actionLoading" :disabled="state.paused">暂停队列</el-button>
            </el-card>
            <el-card class="ops-card" shadow="hover">
              <div class="ops-title"><i class="el-icon-video-play" style="color:#67c23a;"></i> 手动恢复</div>
              <div class="ops-desc">恢复队列运行，重置连续失败计数。已 failed 的 job 不会自动重发。</div>
              <el-button type="success" size="small" @click="handleManualResume" :loading="actionLoading" :disabled="!state.paused">恢复队列</el-button>
            </el-card>
            <el-card class="ops-card" shadow="hover">
              <div class="ops-title"><i class="el-icon-connection" style="color:#409eff;"></i> 立即探活</div>
              <div class="ops-desc">立即向探活群发送心跳消息，验证企微通道是否恢复。</div>
              <el-button type="primary" size="small" @click="handleTriggerProbe" :loading="actionLoading">触发探活</el-button>
            </el-card>
            <el-card class="ops-card ops-card-danger" shadow="hover">
              <div class="ops-title"><i class="el-icon-delete" style="color:#f56c6c;"></i> 清空待发</div>
              <div class="ops-desc">删除所有 waiting/delayed 状态的 job。<b style="color:#f56c6c;">不可恢复！</b></div>
              <el-button type="danger" size="small" @click="handleDrain" :loading="actionLoading">清空队列</el-button>
            </el-card>
          </div>
        </el-tab-pane>

        <!-- Tab 4: 失败消息 -->
        <el-tab-pane name="failed">
          <span slot="label"><i class="el-icon-warning-outline"></i> 失败消息</span>
          <div class="tab-toolbar">
            <el-select v-model="failedFilter.source" clearable placeholder="筛选来源" size="small" style="width: 180px;" @change="searchFailedLogs">
              <el-option v-for="s in sourceOptions" :key="s.value" :label="s.label" :value="s.value"></el-option>
            </el-select>
            <el-date-picker v-model="failedFilter.dateRange" type="datetimerange" size="small"
                            range-separator="至" start-placeholder="开始时间" end-placeholder="结束时间"
                            value-format="yyyy-MM-dd HH:mm:ss" @change="searchFailedLogs"></el-date-picker>
            <el-button size="small" icon="el-icon-refresh" @click="searchFailedLogs">刷新</el-button>
            <el-button type="primary" size="small" icon="el-icon-refresh-right" :disabled="failedSelection.length === 0"
                       :loading="retryLoading" @click="doRetryFailed(failedSelection)">
              重发选中 ({{ failedSelection.length }})
            </el-button>
          </div>
          <el-table :data="failedList" border stripe size="small" v-loading="failedLoading" style="width: 100%;"
                    @selection-change="handleFailedSelectionChange">
            <el-table-column type="selection" width="42" :selectable="row => !isAuditSource(row.source)"></el-table-column>
            <el-table-column prop="sentAt" label="时间" width="160" align="center">
              <template slot-scope="scope">{{ $getDateTime(scope.row.sentAt) }}</template>
            </el-table-column>
            <el-table-column label="来源" width="140">
              <template slot-scope="scope">
                <el-tag size="mini" :type="sourceTagType(scope.row.source)">{{ sourceLabel(scope.row.source) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="conversationName" label="目标群" width="180" show-overflow-tooltip></el-table-column>
            <el-table-column prop="content" label="消息内容" min-width="250" show-overflow-tooltip></el-table-column>
            <el-table-column prop="attemptsMade" label="重试" width="70" align="center"></el-table-column>
            <el-table-column prop="errorMessage" label="错误" min-width="180" show-overflow-tooltip></el-table-column>
            <el-table-column label="操作" width="70" align="center">
              <template slot-scope="scope">
                <el-button v-if="!isAuditSource(scope.row.source)" type="text" size="mini" @click="doRetryFailed([scope.row])">重发</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination @current-change="handleFailedPageChange" :current-page="failedPagenum" :page-size="failedPagesize"
                         :total="failedTotal" layout="total, prev, pager, next" class="page-pagination"></el-pagination>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- Job 详情弹窗（队列实况中查看消息内容与群信息） -->
    <el-dialog title="Job 详情" :visible.sync="jobDetailVisible" width="640px">
      <el-form label-width="90px" size="small" v-if="jobDetail.id">
        <el-form-item label="JobId">{{ jobDetail.id }}</el-form-item>
        <el-form-item label="来源">
          <el-tag size="mini" :type="sourceTagType(jobDetail.source)">{{ sourceLabel(jobDetail.source) }}</el-tag>
        </el-form-item>
        <el-form-item label="目标群">{{ convName(jobDetail.conversationId) }}</el-form-item>
        <el-form-item label="群ID">{{ jobDetail.conversationId }}</el-form-item>
        <el-form-item label="发送人">{{ jobDetail.sentBy }}</el-form-item>
        <el-form-item label="优先级">{{ priorityLabel(jobDetail.priority) }}</el-form-item>
        <el-form-item label="重试次数">{{ jobDetail.attemptsMade }}</el-form-item>
        <el-form-item label="时间">
          <div>入队：{{ jobDetail.timestamp ? $getDateTime(new Date(jobDetail.timestamp)) : '-' }}</div>
          <div>处理：{{ jobDetail.processedOn ? $getDateTime(new Date(jobDetail.processedOn)) : '-' }}</div>
          <div>完成：{{ jobDetail.finishedOn ? $getDateTime(new Date(jobDetail.finishedOn)) : '-' }}</div>
        </el-form-item>
        <el-form-item v-if="jobDetail.failedReason" label="失败原因">
          <span style="color: #f56c6c; word-break: break-all;">{{ jobDetail.failedReason }}</span>
        </el-form-item>
        <el-form-item label="消息内容">
          <pre style="white-space: pre-wrap; word-break: break-all; background: #f5f7fa; padding: 8px; border-radius: 4px; max-height: 220px; overflow: auto; font-size: 12px; margin: 0;">{{ jobDetail.content || '-' }}</pre>
        </el-form-item>
        <el-form-item v-if="jobDetail.logId" label="关联日志">
          <span style="font-size: 12px; color: #909399;">{{ jobDetail.logId }}（=JobId，可在发送日志/失败消息中反查）</span>
        </el-form-item>
      </el-form>
    </el-dialog>
  </div>
</template>

<script>
const SOURCE_MAP = {
  daily_report: { label: '日报定时', type: 'primary' },
  daily_report_manual: { label: '日报手工', type: '' },
  negative_comment: { label: '差评定时', type: 'warning' },
  negative_comment_manual: { label: '差评手工', type: '' },
  patrol_notify: { label: '巡店播报', type: 'success' },
  patrol_notify_store: { label: '巡店店铺推送', type: 'success' },
  patrol_alert: { label: '巡店告警', type: 'danger' },
  manual: { label: '后台手工', type: 'info' },
  probe: { label: '探活', type: 'info' },
  circuit_break: { label: '熔断审计', type: 'danger' },
  queue_resumed: { label: '恢复审计', type: 'success' },
  manual_pause: { label: '手动暂停', type: 'warning' }
}

const PRIORITY_MAP = {
  1: { label: '插队', type: 'danger' },
  2: { label: '探活', type: 'warning' },
  5: { label: '手工', type: '' },
  10: { label: '定时', type: 'info' }
}

export default {
  data () {
    return {
      activeTab: 'jobs',
      autoRefresh: true,
      refreshTimer: null,

      // 状态
      state: {},
      config: {},
      queueCounts: {},
      last24h: {},

      // 配置表单
      configForm: {
        pushMode: 'queue',
        intervalMs: 2000,
        maxConsecutiveFailures: 3,
        probeIntervalMs: 300000,
        autoResendOnResume: true,
        probeConversationId: '',
        probeMessage: '[心跳探测] 企微通道探活，请忽略',
        mail: { enabled: true, host: 'smtp.qq.com', port: 465, secure: true, user: '', pass: '', from: '', to: [] }
      },
      mailToStr: '',
      settingLoading: false,
      saveLoading: false,
      testMailLoading: false,

      // Jobs
      jobState: 'waiting',
      jobDetailVisible: false,
      jobDetail: {},
      jobList: [],
      jobTotal: 0,
      jobPagenum: 1,
      jobPagesize: 20,
      jobLoading: false,

      // 失败消息
      failedFilter: { source: '', dateRange: null },
      failedSelection: [],
      retryLoading: false,
      failedList: [],
      failedTotal: 0,
      failedPagenum: 1,
      failedPagesize: 20,
      failedLoading: false,

      // 群列表（探活群选择用）
      allGroups: [],

      actionLoading: false,

      sourceOptions: Object.keys(SOURCE_MAP).map(k => ({ value: k, label: SOURCE_MAP[k].label }))
    }
  },

  computed: {
    statusText () {
      if (this.config.pushMode === 'direct') return '灰度直连'
      return this.state.paused ? '已暂停' : '运行中'
    },
    statusClass () {
      if (this.config.pushMode === 'direct') return 'status-direct'
      return this.state.paused ? 'status-paused' : 'status-running'
    },
    statusIcon () {
      if (this.config.pushMode === 'direct') return 'el-icon-warning-outline'
      return this.state.paused ? 'el-icon-video-pause' : 'el-icon-video-play'
    },
    pauseReasonText () {
      const r = this.state.pauseReason
      if (r === 'auto_circuit_break') return '自动熔断'
      if (r === 'manual') return '管理员手动暂停'
      return r || '-'
    },
    totalWaiting () {
      return (this.queueCounts.waiting || 0) + (this.queueCounts.prioritized || 0) + (this.queueCounts.paused || 0) +
        (this.queueCounts.delayed || 0) + (this.queueCounts.active || 0)
    },
    // 待发 = wait + prioritized + paused（本队列所有 job 都带 priority，等待中位于 prioritized 集合）
    pendingCount () {
      return (this.queueCounts.waiting || 0) + (this.queueCounts.prioritized || 0) + (this.queueCounts.paused || 0)
    }
  },

  mounted () {
    this.loadStatus()
    this.loadSetting()
    this.loadJobs()
    this.loadAllGroups()
    this.startAutoRefresh()
  },

  beforeDestroy () {
    this.stopAutoRefresh()
  },

  watch: {
    autoRefresh (val) {
      if (val) this.startAutoRefresh()
      else this.stopAutoRefresh()
    }
  },

  methods: {
    startAutoRefresh () {
      this.stopAutoRefresh()
      this.refreshTimer = setInterval(() => {
        this.loadStatus()
        if (this.activeTab === 'jobs') this.loadJobs()
      }, 5000)
    },
    stopAutoRefresh () {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    handleTabClick (tab) {
      if (tab.name === 'jobs') this.loadJobs()
      if (tab.name === 'setting') this.loadSetting()
      if (tab.name === 'failed') this.loadFailedLogs()
    },

    async loadStatus () {
      try {
        const { data: res } = await this.$http.get('yunwei/weworkPush/status')
        if (res.meta.status === 200) {
          this.state = res.data.state || {}
          this.config = res.data.config || {}
          this.queueCounts = res.data.queueCounts || {}
          this.last24h = res.data.last24h || {}
        }
      } catch (e) { /* 忽略 */ }
    },

    async loadSetting () {
      this.settingLoading = true
      try {
        const { data: res } = await this.$http.get('yunwei/weworkPush/setting')
        if (res.meta.status === 200) {
          const cfg = res.data || {}
          this.configForm = {
            pushMode: cfg.pushMode || 'queue',
            intervalMs: cfg.intervalMs || 2000,
            maxConsecutiveFailures: cfg.maxConsecutiveFailures || 3,
            probeIntervalMs: cfg.probeIntervalMs || 300000,
            autoResendOnResume: cfg.autoResendOnResume !== false,
            probeConversationId: cfg.probeConversationId || '',
            probeMessage: cfg.probeMessage || '',
            mail: Object.assign({ enabled: true, host: 'smtp.qq.com', port: 465, secure: true, user: '', pass: '', from: '', to: [] }, cfg.mail || {})
          }
          this.mailToStr = (this.configForm.mail.to || []).join(',')
        }
      } catch (e) {
        this.$message.error('加载配置失败')
      }
      this.settingLoading = false
    },

    async loadAllGroups () {
      try {
        const { data: res } = await this.$http.get('yunwei/wework/allGroups')
        if (res.meta.status === 200) this.allGroups = res.data.list || []
      } catch (e) { /* 忽略 */ }
    },

    async saveSetting () {
      this.saveLoading = true
      try {
        const payload = { ...this.configForm }
        payload.mail = { ...payload.mail, to: this.mailToStr.split(',').map(s => s.trim()).filter(Boolean) }
        const { data: res } = await this.$http.post('yunwei/weworkPush/setting', payload)
        if (res.meta.status === 200) {
          this.$message.success('配置已保存，热更新生效')
          this.loadStatus()
        } else {
          this.$message.error(res.meta.msg || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存失败')
      }
      this.saveLoading = false
    },

    async testMail () {
      this.testMailLoading = true
      try {
        const { data: res } = await this.$http.post('yunwei/weworkPush/testMail', {})
        if (res.meta.status === 200) this.$message.success('测试邮件已发送')
        else this.$message.error(res.meta.msg || '发送失败')
      } catch (e) {
        this.$message.error('请求失败')
      }
      this.testMailLoading = false
    },

    async loadJobs () {
      this.jobLoading = true
      try {
        const { data: res } = await this.$http.get('yunwei/weworkPush/jobs', {
          params: { state: this.jobState, pagenum: this.jobPagenum, pagesize: this.jobPagesize }
        })
        if (res.meta.status === 200) {
          this.jobList = res.data.list || []
          this.jobTotal = res.data.total || 0
        }
      } catch (e) { /* 忽略 */ }
      this.jobLoading = false
    },

    handleJobPageChange (p) {
      this.jobPagenum = p
      this.loadJobs()
    },

    async loadFailedLogs () {
      this.failedLoading = true
      try {
        const params = { pagenum: this.failedPagenum, pagesize: this.failedPagesize, status: 2 }
        if (this.failedFilter.source) params.source = this.failedFilter.source
        // 时间范围筛选（后端 messageLogs 支持 startTime/endTime）
        if (this.failedFilter.dateRange && this.failedFilter.dateRange.length === 2) {
          params.startTime = this.failedFilter.dateRange[0]
          params.endTime = this.failedFilter.dateRange[1]
        }
        const { data: res } = await this.$http.get('yunwei/wework/messageLogs', { params })
        if (res.meta.status === 200) {
          this.failedList = res.data.list || []
          this.failedTotal = res.data.total || 0
        }
      } catch (e) { /* 忽略 */ }
      this.failedLoading = false
    },

    searchFailedLogs () {
      this.failedPagenum = 1
      this.loadFailedLogs()
    },

    // 系统审计/探活类记录不可重发（后端同样有拦截，前端仅控制勾选/按钮可见性）
    isAuditSource (source) {
      return ['circuit_break', 'queue_resumed', 'manual_pause', 'probe'].includes(source)
    },

    handleFailedSelectionChange (val) {
      this.failedSelection = val
    },

    // 重发失败消息（单条/勾选批量共用）：重新入队走完整重试链，原失败记录保留作凭证
    async doRetryFailed (rows) {
      const logIds = rows.map(r => r._id)
      if (logIds.length === 0) return
      const tip = logIds.length === 1
        ? `确认重发该消息到「${rows[0].conversationName || rows[0].conversation_id}」？`
        : `确认重发选中的 ${logIds.length} 条失败消息？`
      this.$confirm(tip, '重发确认', { type: 'warning' }).then(async () => {
        this.retryLoading = true
        try {
          const { data: res } = await this.$http.post('yunwei/weworkPush/retry', { logIds })
          if (res.meta.status === 200) {
            this.$message.success(res.meta.msg || '已重新入队')
            this.loadFailedLogs()
            this.loadStatus()
          } else {
            this.$message.error(res.meta.msg || '重发失败')
          }
        } catch (e) {
          this.$message.error('请求失败')
        }
        this.retryLoading = false
      }).catch(() => {})
    },

    handleFailedPageChange (p) {
      this.failedPagenum = p
      this.loadFailedLogs()
    },

    handleManualPause () {
      this.$confirm('确认手动暂停队列？暂停后新消息不会派发。', '确认暂停', { type: 'warning' }).then(async () => {
        this.actionLoading = true
        try {
          const { data: res } = await this.$http.post('yunwei/weworkPush/pause', {})
          if (res.meta.status === 200) {
            this.$message.success('队列已暂停')
            this.loadStatus()
          } else this.$message.error(res.meta.msg || '操作失败')
        } catch (e) { this.$message.error('请求失败') }
        this.actionLoading = false
      }).catch(() => {})
    },

    handleManualResume () {
      this.$confirm('确认恢复队列运行？连续失败计数将被重置。', '确认恢复', { type: 'warning' }).then(async () => {
        this.actionLoading = true
        try {
          const { data: res } = await this.$http.post('yunwei/weworkPush/resume', {})
          if (res.meta.status === 200) {
            this.$message.success('队列已恢复')
            this.loadStatus()
          } else this.$message.error(res.meta.msg || '操作失败')
        } catch (e) { this.$message.error('请求失败') }
        this.actionLoading = false
      }).catch(() => {})
    },

    async handleTriggerProbe () {
      this.actionLoading = true
      try {
        const { data: res } = await this.$http.post('yunwei/weworkPush/probe', {})
        if (res.meta.status === 200) {
          this.$message.success('探活已触发，请稍后查看结果')
          setTimeout(() => this.loadStatus(), 3000)
        } else this.$message.error(res.meta.msg || '触发失败')
      } catch (e) { this.$message.error('请求失败') }
      this.actionLoading = false
    },

    handleDrain () {
      this.$confirm('确认清空所有 waiting/delayed 状态的消息？此操作不可恢复！', '危险操作', {
        type: 'error', confirmButtonText: '确认清空', cancelButtonText: '取消'
      }).then(async () => {
        this.actionLoading = true
        try {
          const { data: res } = await this.$http.post('yunwei/weworkPush/drain', {})
          if (res.meta.status === 200) {
            this.$message.success('已清空待发队列')
            this.loadStatus()
            this.loadJobs()
          } else this.$message.error(res.meta.msg || '操作失败')
        } catch (e) { this.$message.error('请求失败') }
        this.actionLoading = false
      }).catch(() => {})
    },

    // conversationId → 群名（allGroups 在 mounted 时加载，找不到时显示原始 ID）
    convName (id) {
      if (!id) return '-'
      const g = (this.allGroups || []).find(x => x.conversation_id === id)
      return g ? g.nickname : id
    },

    showJobDetail (row) {
      this.jobDetail = row
      this.jobDetailVisible = true
    },

    sourceLabel (source) { return (SOURCE_MAP[source] && SOURCE_MAP[source].label) || source || '-' },
    sourceTagType (source) { return (SOURCE_MAP[source] && SOURCE_MAP[source].type) || '' },
    priorityLabel (p) { return (PRIORITY_MAP[p] && PRIORITY_MAP[p].label) || '-' },
    priorityTagType (p) { return (PRIORITY_MAP[p] && PRIORITY_MAP[p].type) || '' }
  }
}
</script>

<style scoped>
.push-monitor-page {
  padding: 0 4px;
}
.page-breadcrumb { margin-bottom: 12px; }

.status-card { margin-bottom: 12px; }
.status-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.status-badge-wrap { display: flex; align-items: center; gap: 12px; }
.status-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 16px; font-size: 14px; font-weight: 600;
}
.status-badge i { font-size: 16px; }
.status-running { background: #f0f9eb; color: #67c23a; border: 1px solid #c2e7b0; }
.status-paused { background: #fef0f0; color: #f56c6c; border: 1px solid #fbc4c4; animation: pulse 1.5s infinite; }
.status-direct { background: #f4f4f5; color: #909399; border: 1px solid #d3d4d6; }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }
.paused-info { color: #909399; font-size: 12px; }

.metric-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 12px; }
.metric-box { background: #fafafa; border: 1px solid #ebeef5; border-radius: 6px; padding: 12px; text-align: center; }
.metric-label { color: #909399; font-size: 12px; margin-bottom: 4px; }
.metric-value { font-size: 24px; font-weight: 600; color: #303133; line-height: 1.2; }
.metric-value.metric-success { color: #67c23a; }
.metric-value.metric-danger { color: #f56c6c; }
.metric-value.metric-warning { color: #e6a23c; }
.metric-sub { color: #c0c4cc; font-size: 11px; margin-top: 4px; }

.paused-detail { margin-bottom: 8px; }
.paused-body { font-size: 13px; line-height: 1.8; }
.paused-actions { margin-top: 8px; display: flex; align-items: center; gap: 12px; }
.hint { color: #909399; font-size: 12px; }
.recent-error { margin-top: 8px; }

.content-card { min-height: 500px; }
.tab-toolbar { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
.page-pagination { margin-top: 12px; text-align: right; }

.config-form { max-width: 900px; }
.form-hint { color: #909399; font-size: 12px; margin-top: 4px; line-height: 1.5; }
.form-hint-inline { color: #909399; font-size: 12px; margin-left: 12px; }

.ops-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
.ops-card { border-left: 3px solid #409eff; }
.ops-card-danger { border-left-color: #f56c6c; }
.ops-title { font-size: 14px; font-weight: 600; margin-bottom: 8px; }
.ops-desc { font-size: 12px; color: #606266; margin-bottom: 12px; line-height: 1.6; min-height: 40px; }
</style>
